import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Alert, Spinner } from '@vtex/admin-ui'
import { type ImportResponseProps } from '@/components/products-listing/hooks/useImportRequest'
import { useFeatureFlag } from '@/contexts/feature-flag'
import { localeNumber } from '@/lib/resolveLocale'

import * as style from './ImportAlerts.styles'

export interface ImportError {
  Message?: string
  Code?: string
  Data?: unknown
}

interface ImportAlertsProps {
  importResponse: Record<string, ImportResponseProps>
  importError: ImportError
  actualHash: string
}

const renderAlert = (
  hash: string,
  response: ImportResponseProps,
  responseType: string,
  parseType: Record<string, string>,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
  handleDismissAlert: (hash: string) => void
) => {
  const currentStatus = response?.Status
  const currentFileLink = response?.FileLink
  const currentParseType = parseType[responseType] || parseType.file

  // Rule: If status is 'InProgress' or 'New', show progress alert
  if (currentStatus === 'InProgress' || currentStatus === 'New') {
    return (
      <Alert
        key={hash}
        className={`${style.alertIcon} ${style.alert}`}
        variant="info"
      >
        <Spinner size={16} />
        <FormattedMessage
          id="admin/admin.catalog.import-alerts.progress-message"
          defaultMessage="Importing {type}. You'll receive an email when import is complete."
          values={{ type: currentParseType }}
        />
      </Alert>
    )
  }

  // Rule: If FileLink is not empty, show warning alert with download
  if (currentFileLink && currentFileLink !== '') {
    return (
      <Alert
        key={hash}
        className={style.alert}
        variant="warning"
        action={{
          children: formatMessage({
            id: 'admin/admin.catalog.import-alerts.warning-action',
            defaultMessage: 'Download error report',
          }),
          onClick: () => (window.location.href = currentFileLink || ''),
        }}
        onDismiss={() => handleDismissAlert(hash)}
      >
        <FormattedMessage
          id="admin/admin.catalog.import-alerts.warning-message"
          defaultMessage="The {type} couldn't be imported due to errors. Download the report, correct the errors, and import the file again."
          values={{
            type: currentParseType,
          }}
        />
      </Alert>
    )
  }

  // Rule: If status is 'Completed' and FileLink is empty, show success
  if (currentStatus === 'Completed' && currentFileLink === null) {
    return (
      <Alert
        key={hash}
        className={style.alert}
        variant="positive"
        onDismiss={() => handleDismissAlert(hash)}
      >
        <FormattedMessage
          id="admin/admin.catalog.import-alerts.success-message"
          defaultMessage="{type} imported successfully"
          values={{
            type:
              currentParseType.charAt(0).toUpperCase() +
              currentParseType.slice(1).toLowerCase(),
          }}
        />
      </Alert>
    )
  }

  return null
}

export const ImportAlerts = ({
  importResponse,
  importError,
  actualHash,
}: ImportAlertsProps) => {
  const { formatMessage, locale } = useIntl()
  const [showImportAlert, setShowImportAlert] = useState(false)
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set())
  const importRowLimit = useFeatureFlag<number>('import_limit', 2000)

  const parseType = {
    Product: formatMessage({
      id: 'admin/admin.catalog.import-types-products-and-skus',
      defaultMessage: 'products and SKUs',
    }),
    Image: formatMessage({
      id: 'admin/admin.catalog.import-types-images',
      defaultMessage: 'images',
    }),
    ProductSpecification: formatMessage({
      id: 'admin/admin.catalog.import-types-product-specifications',
      defaultMessage: 'product specifications',
    }),
    SkuSpecification: formatMessage({
      id: 'admin/admin.catalog.import-types-sku-specifications',
      defaultMessage: 'SKU specifications',
    }),
    file: formatMessage({
      id: 'admin/admin.catalog.import-types-placeholder-file',
      defaultMessage: 'file',
    }),
  }

  useEffect(() => {
    if (actualHash || Object.keys(importError).length > 0) {
      setShowImportAlert(true)
    }
  }, [actualHash, importError])

  const handleDismissAlert = (hash: string) => {
    setDismissedAlerts((prev) => new Set([...prev, hash]))
  }

  if (!showImportAlert) return null

  if (
    Object.keys(importError).length > 0 &&
    importError.Code === 'NOT_MAPPED'
  ) {
    return (
      <Alert
        className={style.alert}
        variant="critical"
        /* TODO: Action will be implemented soon
        action={{
          children: formatMessage({
            id: 'admin/admin.catalog.import-alerts.error-action',
            defaultMessage: 'Saiba mais',
          }),
          onClick: () => {},
        }}
        */
        onDismiss={() => setShowImportAlert(false)}
      >
        <FormattedMessage
          id="admin/admin.catalog.import-alerts.error-message-description"
          defaultMessage="File couldn't be processed due to a technical problem: [{errorDescription}]"
          values={{ errorDescription: importError.Message }}
        />
      </Alert>
    )
  }

  if (
    Object.keys(importError).length > 0 &&
    importError.Code === 'IMPORT_LIMIT_EXCEEDED'
  ) {
    return (
      <Alert
        className={style.alert}
        variant="critical"
        onDismiss={() => setShowImportAlert(false)}
      >
        <FormattedMessage
          id="admin/admin.catalog.import-alerts.error-message-row-limit"
          defaultMessage="File couldn't be processed due to a technical problem: The spreadsheet exceeds the limit of {importRowLimit} rows. Please reduce the number of rows and try again."
          values={{ importRowLimit: localeNumber(importRowLimit, locale) }}
        />
      </Alert>
    )
  }

  if (
    Object.keys(importError).length > 0 &&
    importError.Code === 'IMPORT_FILE_SIZE_LIMIT_EXCEEDED'
  ) {
    return (
      <Alert
        className={style.alert}
        variant="critical"
        onDismiss={() => setShowImportAlert(false)}
      >
        <FormattedMessage
          id="admin/admin.catalog.import-alerts.error-message-size-limit"
          defaultMessage="File couldn't be processed due to a technical problem: The file size exceeds the 20 MB limit. Please reduce the size of the file and try again."
        />
      </Alert>
    )
  }

  if (
    Object.keys(importError).length > 0 &&
    importError.Code === 'IMPORT_REQUEST_LIMIT_EXCEEDED_BY_ACCOUNT'
  ) {
    return (
      <Alert
        className={style.alert}
        variant="critical"
        onDismiss={() => setShowImportAlert(false)}
      >
        <FormattedMessage
          id="admin/admin.catalog.import-alerts.error-message-limit-exceeded-by-account"
          defaultMessage="File couldn't be processed due to a technical problem. There is already an import process occurring for this account."
        />
      </Alert>
    )
  }

  if (Object.keys(importError).length > 0) {
    return (
      <Alert
        className={style.alert}
        variant="critical"
        onDismiss={() => setShowImportAlert(false)}
      >
        <FormattedMessage
          id="admin/admin.catalog.import-alerts.error-message-generic"
          defaultMessage="File couldn't be processed due to a technical problem. Try again in a few moments or contact support if the issue persists."
        />
      </Alert>
    )
  }

  return (
    <>
      {Object.entries(importResponse)
        .filter(([hash]) => !dismissedAlerts.has(hash))
        .map(([hash, response]) =>
          renderAlert(
            hash,
            response,
            response.Type,
            parseType,
            formatMessage,
            handleDismissAlert
          )
        )}
    </>
  )
}
